import {
  Aggregator,
  AlertHistoryRequestDTO,
  SilenceRequest,
} from "@energy-advice/alertfan-api-client/dist/lib/generated/api";

import { Silence } from "@energy-advice/alertfan-api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertFanApi } from "../AlertfanApi";
import {
  defaultMutationOptions,
  defaultResponseHandler,
  IMutationOptions,
  IQueryOptions,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";

// Alert hooks ---------------------------------------------------------------------------------------------------------
export const useGetAlertFan = (refTime: string, options?: IQueryOptions) =>
  useQueryWrapper([QueryKey.ALERT_FAN], () => AlertFanApi.alertsApi.getAlerts(), null, options);
// export const isAlertFansActive = () => useQueryWrapper([QueryKey.ALERT_FAN], ()=> AlertFanApi.alertsApi.alertfanAlertsIsActivePost())

// Tags hooks ----------------------------------------------------------------------------------------------------------
// Silences hooks ------------------------------------------------------------------------------------------------------
export const useGetSilences = (showExpired?: boolean) =>
  useQueryWrapper([QueryKey.ALERT_FAN_SILENCE, showExpired ? "true" : "false"], () =>
    AlertFanApi.silencesApi.getSilences(showExpired)
  );

export const useGetSilence = (id: string) =>
  useQueryWrapper([QueryKey.ALERT_FAN_SILENCE, id], () => AlertFanApi.silencesApi.getSilence(id));

export const useAddSilence = (showExpired?: boolean, specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (silence: SilenceRequest) =>
      AlertFanApi.silencesApi.addSilence(silence).then(defaultResponseHandler),
    onSuccess: async res => {
      await AlertFanApi.silencesApi.getSilence(res as string).then(data => {
        queryClient.setQueryData([QueryKey.ALERT_FAN_SILENCE, showExpired ? "true" : "false"], (e: any) => {
          return data.data ? [data.data, ...e] : e;
        });
      });

      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
};

export const useDeleteSilence = (showExpired?: boolean, specOptions?: IMutationOptions) => {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();
  let idHolder = "";
  return useMutation({
    mutationFn: (id: string) => {
      idHolder = id;
      return AlertFanApi.silencesApi.deleteSilence(id).then(defaultResponseHandler);
    },
    onSuccess: () => {
      queryClient.setQueryData([QueryKey.ALERT_FAN_SILENCE, showExpired ? "true" : "false"], (e: any) => {
        return e.filter((o: Silence) => o.id !== idHolder);
      });

      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
};

// History hooks -------------------------------------------------------------------------------------------------------
function concatenateProperties(dto: AlertHistoryRequestDTO): string {
  const properties: string[] = [];

  if (dto.containingTags) {
    properties.push(dto.containingTags.join(","));
  }

  if (dto.notContainingTags) {
    properties.push(dto.notContainingTags.join(","));
  }

  if (dto.searchText) {
    properties.push(dto.searchText);
  }

  if (dto.start) {
    properties.push(dto.start);
  }

  if (dto.end) {
    properties.push(dto.end);
  }

  return properties.join(",");
}

export const useGetAlertsHistory = (alertHistory: AlertHistoryRequestDTO, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.ALERT_FAN, concatenateProperties(alertHistory)],
    () => AlertFanApi.historyApi.getHistory(alertHistory),
    null,
    options
  );

export const useGetAggregators = (options?: IQueryOptions) => {
  return useQueryWrapper(
    [QueryKey.ALERTS_AGGREGATORS],
    () => AlertFanApi.configurationApi.getAggregators(),
    null,
    options
  );
};

export const useGetSenders = (options?: IQueryOptions) => {
  return useQueryWrapper(
    [QueryKey.ALERTS_SENDERS],
    () => AlertFanApi.configurationApi.getSenders(),
    null,
    options
  );
};

export const useUpdateAggregators = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (aggregators: Array<Aggregator>) => {
      return AlertFanApi.configurationApi.setAggregators(aggregators);
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.ALERTS_AGGREGATORS] });
      options.onSuccess!!();
    },
  });
};
